import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router";
import { SignInButton, SignedIn, SignedOut, UserButton } from "@clerk/react-router";
import { X } from "lucide-react";
import { Race } from "@/backend/domain/models/race";
import { getRaceStatusConfig } from "./mainNavigation";

interface MobileMenuProps {
    races: Race[];
    isAdmin: boolean;
    isOpen: boolean;
    onClose: () => void;
}

export function MobileMenu({ races, isAdmin, isOpen, onClose }: MobileMenuProps) {
    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/20 z-40 md:hidden"
                        onClick={onClose}
                    />

                    {/* Menu Panel */}
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 20, stiffness: 300 }}
                        className="fixed right-0 top-0 bottom-0 w-80 bg-white z-50 md:hidden shadow-xl flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-6 border-b flex justify-between items-center">
                            <span className="font-light text-lg tracking-wider">MENÜ</span>
                            <Button variant="ghost" size="icon" onClick={onClose}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto">
                            <div className="p-6 space-y-8">
                                {/* Races Section */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-medium text-slate-500 tracking-wider">RENNEN</h3>
                                    <div className="space-y-2">
                                        {races.length > 0 ? (
                                            races.map((race) => {
                                                const statusConfig = getRaceStatusConfig(race.status);
                                                const StatusIcon = statusConfig.icon;

                                                return (
                                                    <Link
                                                        key={race.id}
                                                        to={`/race/${race.id}`}
                                                        onClick={onClose}
                                                        className="block"
                                                    >
                                                        <div className="p-3 rounded-lg hover:bg-slate-50 transition-colors">
                                                            <div className="font-medium mb-1">{race.name}</div>
                                                            <div className="flex flex-col gap-1 text-sm">
                                <span className="text-slate-600">
                                  {new Date(race.date).toLocaleDateString("de-DE", {
                                      day: "numeric",
                                      month: "long",
                                  })}
                                </span>
                                                                <span className={`flex items-center gap-1.5 ${statusConfig.className}`}>
                                  <StatusIcon className="h-3.5 w-3.5" />
                                  <span>{statusConfig.text}</span>
                                </span>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                );
                                            })
                                        ) : (
                                            <div className="text-sm text-slate-600 p-3">
                                                Keine aktiven Rennen
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* My Races & Admin Section */}
                                <SignedIn>
                                    <div className="space-y-4">
                                        <h3 className="text-sm font-medium text-slate-500 tracking-wider">KONTO</h3>
                                        <div className="space-y-2">
                                            <Link
                                                to="/myraces"
                                                onClick={onClose}
                                                className="block p-3 rounded-lg hover:bg-slate-50 transition-colors"
                                            >
                                                Meine Rennen
                                            </Link>
                                            {isAdmin && (
                                                <Link
                                                    to="/admin"
                                                    onClick={onClose}
                                                    className="block p-3 rounded-lg hover:bg-slate-50 transition-colors"
                                                >
                                                    Administration
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </SignedIn>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t mt-auto">
                            <SignedOut>
                                <SignInButton mode="modal">
                                    <Button
                                        variant="default"
                                        className="w-full"
                                        onClick={onClose}
                                    >
                                        LOGIN
                                    </Button>
                                </SignInButton>
                            </SignedOut>
                            <SignedIn>
                                <div className="flex justify-center">
                                    <UserButton afterSignOutUrl="/" />
                                </div>
                            </SignedIn>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
